/**
 * The twelve phases and the gates each can raise. `Eia.Core/Phases/PhaseTable.cs`, which is the
 * authority — this is a projection of it for the rail, not a second definition.
 *
 * <p><b>THE RAIL SHOWS PHASES THE RUN HAS NOT REACHED</b> (`AC-UI-6`), so it cannot be built from
 * the event stream alone: an engagement at phase 3 has emitted three phase events and the reader
 * needs to see all twelve. The names are the phase table's own, title-cased for display only.</p>
 *
 * <p>The GATES under each phase are the kinds that phase CAN raise. A gate that did not fire is
 * rendered as skipped rather than omitted — "a gate nobody was asked is a fact about the run".</p>
 */
export interface PhaseRow {
  number: number
  name: string
  gates: readonly string[]
}

export const PHASES: readonly PhaseRow[] = [
  { number: 0, name: 'Triage', gates: [] },
  { number: 1, name: 'Classify', gates: [] },
  { number: 2, name: 'Scope', gates: [] },
  { number: 3, name: 'Methods', gates: ['capability', 'ratify'] },
  { number: 4, name: 'Parameters', gates: ['geography'] },
  { number: 5, name: 'Resolve', gates: ['suppression', 'conversion'] },
  { number: 6, name: 'Validity', gates: ['framing', 'assumption_override'] },
  { number: 7, name: 'Freeze', gates: [] },
  { number: 8, name: 'Execute', gates: [] },
  { number: 9, name: 'Verify', gates: [] },
  { number: 10, name: 'Uncertainty', gates: [] },
  { number: 11, name: 'Report', gates: ['sign'] },
]

/**
 * Phases 2 to 5 are ONE CONVERGENCE LOOP, not four stations (`Specification/01 §6.2`). The rail
 * draws a bracket around them, and a re-entry is a second pass rather than a step backwards.
 */
export const LOOP_FROM = 2
export const LOOP_TO = 5

/** The freeze boundary. Everything after it is computed from a specification that cannot change. */
export const FREEZE = 7

/** A gate kind as a reader sees it. The ten are a closed vocabulary and must not be truncated. */
export function gateLabel(kind: string): string {
  return kind.replace(/_/g, ' ')
}

/** `clarify` and `approve_binding` are raised BY A SPECIALIST, at whatever phase it is running. */
export const AGENT_RAISED = ['clarify', 'approve_binding'] as const

/**
 * Which phase writes each specification key. `Eia.Core/Phases/PhaseTable.cs`'s `writes` tuples,
 * which doc 03 §10.6 MUSTs agree exactly with the key list.
 *
 * <p>THE DRAWER SHOWS A KEY BEFORE IT EXISTS, marked pending. A panel that listed only what had
 * been committed would grow from nothing and never tell a reader what is still to come — and
 * `AC-UI-7` asks that it "gains exactly the declared keys as each phase commits", which is a
 * statement about a list that was already there.</p>
 */
export const KEY_PHASE: Readonly<Record<string, string>> = {
  provenance_of_this_spec: 'ph0',
  triage: 'ph0',
  classification: 'ph1',
  scope: 'ph2',
  documents: 'ph2',
  data_layer_status: 'ph3',
  methods: 'ph3',
  capability_boundary: 'ph3',
  parameters: 'ph4',
  graph: 'ph5',
  assumptions_evaluated: 'ph6',
  coverage: 'ph6',
  results: 'ph8',
  flags: 'ph9',
  sensitivity: 'ph10',
  publication_policy: 'ph10',
  report: 'ph11',
  signature: 'ph11',

  // APPEND-ONLY, and written by five phases. It has no single owner and the panel says so rather
  // than picking one.
  decisions_recorded: 'ph3–11',
}

/** The keys in the order the drawer lists them: the order they are committed in. */
export const SPEC_KEYS: readonly string[] = Object.keys(KEY_PHASE)

/**
 * WHICH SPECIALIST SPEAKS IN EACH PHASE. `Eia.Core/Phases/PhaseTable.cs`'s `specialists` tuples.
 *
 * <p>`EXECUTION-MECHANICS` opens with "in this system there are seven agents, called specialists".
 * The UI says AGENT, because that is the word the reader already has — and it says it explicitly
 * rather than leaving them to infer it. The names are the specification's own; no persona is
 * invented, and the word "Agent" is part of the name rather than a suffix a reader has to supply:
 * `Frontend/engagement.html` writes "raised by the Framing &amp; Scoping Agent".</p>
 *
 * <p>It is derived from the PHASE because the stream does not carry a speaker — a `token` frame has
 * no author, and putting a name on prose nobody signed would be inventing one.</p>
 */
export function agentFor(phase: number | null): { name: string; monogram: string; badge: string } {
  if (phase === null) return { name: 'Economic Impact Advisor', monogram: 'EIA', badge: '' }
  if (phase <= 2) return { name: 'Framing & Scoping Agent', monogram: 'F&S', badge: 'ds' }
  if (phase === 3) return { name: 'Analytical Agent', monogram: 'AN', badge: 'an' }
  if (phase === 4) return { name: 'Parameterisation & Evidence Agent', monogram: 'P&E', badge: 'pe' }
  if (phase === 6 || phase === 9) return { name: 'Verification Agent', monogram: 'VE', badge: 've' }
  if (phase === 10) return { name: 'Uncertainty Agent', monogram: 'UN', badge: '' }
  if (phase === 11) return { name: 'Reporting Agent', monogram: 'RP', badge: '' }

  // Phases 5, 7 and 8 are MODEL-FREE. Nothing speaks in them, and a card raised there is the
  // harness's own — which renders no attribution at all.
  return { name: 'Economic Impact Advisor', monogram: 'EIA', badge: '' }
}
